import { useState } from 'react';
import { Box, Card, TextField, Button, Typography, Alert, Stepper, Step, StepLabel, Link } from '@mui/material';
import * as api from '../../api/authApi';
import { PasswordField } from '../../components/common/PasswordField';

const isPasswordValid = pw =>
  pw.length >= 8 && /[A-Z]/.test(pw) && /[a-z]/.test(pw) && /[0-9]/.test(pw);

export default function ForgotPasswordPage() {
  const [step, setStep]       = useState(0);
  const [email, setEmail]     = useState('');
  const [token, setToken]     = useState('');
  const [pwd, setPwd]         = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const sendEmail = async () => {
    setLoading(true); setError('');
    try { await api.forgotPassword({ email }); setStep(1); }
    catch (e) { setError(e.response?.data?.message || 'Error sending reset email.'); }
    setLoading(false);
  };

  const resetPwd = async () => {
    setError('');
    if (!isPasswordValid(pwd)) { setError('Password does not meet the requirements below.'); return; }
    if (pwd !== confirm) { setError('Passwords do not match.'); return; }
    setLoading(true);
    try { await api.resetPassword({ email, token, newPassword: pwd, confirmPassword: confirm }); setStep(2); }
    catch (e) { setError(e.response?.data?.message || 'Error resetting password.'); }
    setLoading(false);
  };

  return (
    <Box minHeight="100vh" display="flex" alignItems="center" justifyContent="center"
      sx={{ background: 'linear-gradient(135deg,#ede9fe 0%,#e0e7ff 50%,#f0f9ff 100%)' }}>
      <Box width="100%" maxWidth={440} px={2}>
        <Box textAlign="center" mb={3}>
          <Box display="inline-flex" alignItems="center" gap={1.5} mb={1}>
            <Box sx={{
              width: 36, height: 36, borderRadius: 2,
              background: 'linear-gradient(135deg,#4f46e5,#818cf8)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Typography sx={{ color: '#fff', fontWeight: 800, fontFamily: "'Sora',sans-serif" }}>A</Typography>
            </Box>
            <Typography variant="h5" fontWeight={800} fontFamily="'Sora',sans-serif" color="#0f172a">Align</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">Reset your password</Typography>
        </Box>
        <Card sx={{ p: 3.5, borderRadius: 3 }}>
          <Stepper activeStep={step} sx={{ mb: 3 }}>
            {['Email', 'New Password', 'Done'].map(l => <Step key={l}><StepLabel>{l}</StepLabel></Step>)}
          </Stepper>
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
          {step === 0 && (
            <Box display="flex" flexDirection="column" gap={2.5}>
              <TextField label="Email address" type="email" value={email}
                onChange={e => setEmail(e.target.value)} fullWidth required />
              <Button variant="contained" onClick={sendEmail} disabled={loading || !email} size="large">
                {loading ? 'Sending…' : 'Send Reset Token'}
              </Button>
            </Box>
          )}
          {step === 1 && (
            <Box display="flex" flexDirection="column" gap={2.5}>
              <Alert severity="info">Check the server logs for your reset token (dev mode).</Alert>
              <TextField label="Reset Token" value={token}
                onChange={e => setToken(e.target.value)} fullWidth required />
              <PasswordField label="New Password" value={pwd}
                onChange={e => setPwd(e.target.value)} showHints />
              <PasswordField label="Confirm Password" value={confirm}
                onChange={e => setConfirm(e.target.value)} showHints={false} />
              <Button variant="contained" onClick={resetPwd}
                disabled={loading || !token || !pwd || !confirm} size="large">
                {loading ? 'Resetting…' : 'Reset Password'}
              </Button>
            </Box>
          )}
          {step === 2 && (
            <Box textAlign="center">
              <Alert severity="success" sx={{ mb: 2.5 }}>Password reset successfully!</Alert>
              <Button variant="contained" href="/login">Back to Login</Button>
            </Box>
          )}
        </Card>
        <Box textAlign="center" mt={2}>
          <Link href="/login" variant="body2" underline="hover" color="primary">← Back to login</Link>
        </Box>
      </Box>
    </Box>
  );
}
